import React, { useEffect, useRef, useState } from "react";
import { AlertCircle } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../../dataManagement/uiDb/UiDb";
import DineInThunk from "../../../../services/thunks/DineInThunk";
import {
  selectDineInCodeError,
  selectDineInHostName,
  selectDineInLoadingMap,
  clearCodeError,
} from "../../../../dataManagement/uiDb/DineInDb";
import "./styles/DineInCodeEntry.css";

interface DineInCodeEntryProps {
  businessId: string;
  tableNumber: string;
}

/** The table code is a fixed six-letter word — easy to read aloud/share. */
const CODE_LENGTH = 6;

const onlyLetters = (value: string) => value.replace(/[^a-zA-Z]/g, "").toUpperCase();

/**
 * Shown when a session already exists for this table (openTable came back
 * INVALID_OR_MISSING_SESSION_CODE). Six single-letter boxes with auto-advance
 * focus, matching the mockup's segmented look but sized for the real
 * fixed-length alphabetic code rather than the 4-digit numeric placeholder.
 */
const DineInCodeEntry: React.FC<DineInCodeEntryProps> = ({ businessId, tableNumber }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [letters, setLetters] = useState<string[]>(Array(CODE_LENGTH).fill(""));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const codeError = useSelector(selectDineInCodeError);
  const hostName = useSelector(selectDineInHostName);
  const loadingMap = useSelector(selectDineInLoadingMap);
  const isJoining = !!loadingMap.joinTable;

  const code = letters.join("");
  const isComplete = code.length === CODE_LENGTH;

  // Wrong code came back — clear the boxes and let them retype from scratch
  // rather than leaving a code we know is wrong sitting there.
  useEffect(() => {
    if (codeError) {
      setLetters(Array(CODE_LENGTH).fill(""));
      inputRefs.current[0]?.focus();
    }
  }, [codeError]);

  const focusBox = (index: number) => inputRefs.current[index]?.focus();

  const handleChange = (index: number, rawValue: string) => {
    if (codeError) dispatch(clearCodeError());
    const value = onlyLetters(rawValue);

    if (value.length > 1) {
      // Fast typing or an autofill suggestion can hand us more than one
      // character at once — spread it across the remaining boxes.
      const chars = value.split("").slice(0, CODE_LENGTH - index);
      setLetters((prev) => {
        const next = [...prev];
        chars.forEach((ch, i) => {
          if (index + i < CODE_LENGTH) next[index + i] = ch;
        });
        return next;
      });
      focusBox(Math.min(index + chars.length, CODE_LENGTH - 1));
      return;
    }

    setLetters((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
    if (value && index < CODE_LENGTH - 1) focusBox(index + 1);
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !letters[index] && index > 0) {
      focusBox(index - 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = onlyLetters(e.clipboardData.getData("text"));
    if (!pasted) return;
    if (codeError) dispatch(clearCodeError());
    setLetters((prev) => {
      const next = [...prev];
      pasted.split("").slice(0, CODE_LENGTH).forEach((ch, i) => {
        next[i] = ch;
      });
      return next;
    });
    focusBox(Math.min(pasted.length, CODE_LENGTH - 1));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isComplete || isJoining) return;
    dispatch(DineInThunk.joinTable({ businessId, tableNumber, sessionCode: code }));
  };

  return (
    <form className="dine-in-code-entry" onSubmit={handleSubmit}>
      <p className="dine-in-code-entry__hint">
        This table is already occupied — ask {hostName ?? "the host"} for the
        6-letter table code to join. Still stuck? Flag a waiter.
      </p>

      <div className="dine-in-code-entry__boxes">
        {letters.map((letter, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            className={`dine-in-code-entry__box${
              codeError ? " dine-in-code-entry__box--error" : ""
            }`}
            value={letter}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            maxLength={1}
            inputMode="text"
            autoCapitalize="characters"
            autoComplete="off"
            autoFocus={index === 0}
            aria-label={`Letter ${index + 1} of ${CODE_LENGTH}`}
          />
        ))}
      </div>
      <p className="dine-in-code-entry__caption">Six letters, no spaces.</p>

      {codeError && (
        <p className="dine-in-code-entry__error">
          <AlertCircle size={14} strokeWidth={2.5} />
          {codeError}
        </p>
      )}

      <div className="dine-in-code-entry__actions">
        <button
          type="submit"
          className="dine-in-code-entry__submit-btn"
          disabled={!isComplete || isJoining}
        >
          {isJoining ? "Joining table..." : "Join table"}
        </button>
      </div>
    </form>
  );
};

export default DineInCodeEntry;
